<template>
  <section class="aboutus-section aboutus-lab-section">
    <div class="title">
      <h2 class="aboutus-title">{{ t("aboutUs.laboratory.title") }}</h2>
      <div class="aboutus-title-underline"></div>
    </div>
    <div class="mainContainer">
      <button class="odm-partner-arrow left" @click="prevDevice" :disabled="!canGoPrev">
        <img :src="require('@/assets/homePage/homeCarousel/arrow_left.png')" alt="左箭头" class="arrow-image" />
      </button>

      <div class="aboutus-lab-row">
        <div class="aboutus-lab-scroll">
          <div ref="scrollContainer" class="aboutus-lab-grid" :style="{
            transform: `translateX(-${scrollOffset}px)`,
            transition: isTransitioning ? 'transform 0.5s ease' : 'none',
          }">
            <div class="aboutus-lab-item" v-for="(device, index) in extendedLabDevices" :key="index">
              <img :src="device.img" :alt="device.label" />
              <div class="aboutus-lab-label">{{ device.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <button class="odm-partner-arrow right" @click="nextDevice" :disabled="!canGoNext">
        <img :src="require('@/assets/homePage/homeCarousel/arrow_right.png')" alt="右箭头" class="arrow-image" />
      </button>
    </div>

    <div class="odm-partner-dots">
      <span v-for="(dot, index) in dots" :key="index" :class="['dot', { active: currentIndex === index }]"
        @click="goToSlide(index)" />
    </div>
  </section>
</template>
<script setup>
import { useI18n } from "vue-i18n";
import { computed, ref, onMounted, onUnmounted, watch } from "vue";

const { t, tm } = useI18n();
const itemsPerGroup = 4;
const autoPlayTimer = ref(null);
const isAutoPlaying = ref(true);
const currentIndex = ref(0);
const isTransitioning = ref(false);
const scrollContainer = ref(null);
const cardWidth = ref(0);
const gapWidth = ref(0);

// 原始设备数据
const labDevicesRaw = computed(() => tm("aboutUs.laboratory.devices") || []);

// 扩展设备列表：尾部追加 itemsPerGroup 个元素（实现无限循环）
const extendedLabDevices = computed(() => {
  const original = labDevicesRaw.value;
  const cloned = original.slice(0, itemsPerGroup);
  return [...original, ...cloned];
});

// 页面计算
const totalPages = computed(() =>
  Math.ceil(labDevicesRaw.value.length / itemsPerGroup)
);
const maxIndex = computed(() => totalPages.value - 1);
const dots = computed(() =>
  Array.from({ length: totalPages.value }, (_, index) => index)
);

// 偏移量计算
const scrollOffset = computed(() => {
  return (
    currentIndex.value * (cardWidth.value + gapWidth.value) * itemsPerGroup
  );
});

// 自动播放
const startAutoPlay = () => {
  if (autoPlayTimer.value) clearInterval(autoPlayTimer.value);
  autoPlayTimer.value = setInterval(() => {
    if (isAutoPlaying.value) {
      nextDevice();
    }
  }, 2500);
};
const stopAutoPlay = () => {
  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value);
    autoPlayTimer.value = null;
  }
};

// 切换下一组
const nextDevice = () => {
  isTransitioning.value = true;
  currentIndex.value++;
  startAutoPlay(); // 重置播放
};

// 切换上一组
const prevDevice = () => {
  isTransitioning.value = true;
  if (currentIndex.value <= 0) {
    currentIndex.value = maxIndex.value;
  } else {
    currentIndex.value--;
  }
  startAutoPlay();
};

// 点切换
const goToSlide = (index) => {
  if (index !== currentIndex.value) {
    isTransitioning.value = true;
    currentIndex.value = index;
  }
};

// 判断滚动是否越界（到达克隆段）
watch(currentIndex, (newIndex) => {
  if (newIndex > maxIndex.value) {
    setTimeout(() => {
      isTransitioning.value = false; // 清除动画
      currentIndex.value = 0;
    }, 500); // 与 transition 时间一致
  }
});

// 宽度初始化
const initCardDimensions = () => {
  if (scrollContainer.value) {
    const cardElement =
      scrollContainer.value.querySelector(".aboutus-lab-item");
    if (cardElement) {
      cardWidth.value = cardElement.offsetWidth;
      gapWidth.value =
        parseInt(getComputedStyle(scrollContainer.value).gap) || 0;
    }
  }
};
const handleResize = () => initCardDimensions();

// 启动
onMounted(() => {
  initCardDimensions();
  window.addEventListener("resize", handleResize);
  startAutoPlay();
});
onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  stopAutoPlay();
});

// 永远允许左右切换（因为是无限轮播）
const canGoPrev = computed(() => true);
const canGoNext = computed(() => true);
</script>


<style scoped>
.title {
  text-align: center;
  margin-bottom: 80px;
}

.aboutus-title {
  text-align: center;
  font-size: 2.2rem;
  font-weight: bold;
  color: #222;
  margin-bottom: 8px;
  position: relative;
}

.aboutus-title-underline {
  width: 80px;
  height: 4px;
  background: #ff7f1a;
  margin: 10px auto 40px auto;
}

.aboutus-lab-section {
  position: relative;
  padding: 20px 0;
  background: #f7f7f7;
  margin-top: 80px;
  /* background:url("../../assets/aboutUs/lab_bg.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;  */
}

.mainContainer {
  position: relative;
  padding: 10px 0;
}

.odm-partner-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 5%;
  border: none;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
}

.odm-partner-arrow.left {
  left: 0;
}

.odm-partner-arrow.right {
  right: 0;
}

.odm-partner-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.arrow-image {
  width: 50%;
}

.arrow-image:hover {
  transform: scale(1.05);
}

.aboutus-lab-row {
  width: 90%;
  margin: 0 auto;
}

.aboutus-lab-scroll {
  overflow: hidden;
}

.aboutus-lab-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
}

.aboutus-lab-item {
  flex-shrink: 0;
  width: calc((100% - 3 * 20px - 8 * 30px) / 4);
  background-color: white;
  padding: 30px;
  border-radius: 5px;
  /* 添加以下样式 */
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.aboutus-lab-item img {
  width: 100%;
  object-fit: cover;
  display: block;
}

.aboutus-lab-label {
  margin-top: 12px;
  text-align: center;
  font-size: 16px;
  color: #333;
}

.odm-partner-dots {
  margin-top: 50px;
  text-align: center;
  margin-bottom: 50px;
}

.dot {
  display: inline-block;
  width: 15px;
  height: 15px;
  margin: 0 6px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  border: solid 1px #ff6b00;
}

.dot.active {
  background: #ff6b00;
}

/* 大屏幕设备 (1400px 以上) */
@media screen and (min-width: 1400px) {
  .aboutus-title {
    font-size: 2.4rem;
  }

  .aboutus-title-underline {
    width: 80px;
  }

  .aboutus-lab-label {
    font-size: 1.4rem;
  }

  .aboutus-title-underline {
    width: 80px;
  }

  .aboutus-lab-section {
    margin-top: 80px;
  }

  .title {
    margin-bottom: 80px;
  }

  .odm-partner-dots {
    margin-top: 50px;
    margin-bottom: 50px;
  }

  .aboutus-lab-item {
    width: calc((100% - 3 * 20px - 8 * 30px) / 4);
    padding: 30px;
  }

  .arrow-image {
    width: 50%;
  }


}

/* 笔记本电脑 */
@media screen and (max-width: 1399px) {
  .aboutus-title {
    font-size: 2.2rem;
  }

  .aboutus-title-underline {
    width: 80px;
  }

  .aboutus-lab-label {
    font-size: 1.3rem;
  }

  .dot {
    width: 13px;
    height: 14px;
  }

  .aboutus-title-underline {
    width: 70px;
  }

  .aboutus-lab-section {
    margin-top: 70px;
  }

  .title {
    margin-bottom: 70px;
  }

  .odm-partner-dots {
    margin-top: 40px;
    margin-bottom: 40px;
  }

  .aboutus-lab-item {
    width: calc((100% - 3 * 20px - 8 * 20px) / 4);
    padding: 20px;
  }

  .arrow-image {
    width: 60%;
  }

}

/* 平板设备 */
@media screen and (max-width: 991px) {
  .aboutus-title {
    font-size: 2rem;
  }

  .aboutus-title-underline {
    width: 80px;
  }

  .aboutus-lab-label {
    font-size: 1.1rem;
  }

  .dot {
    width: 12px;
    height: 12px;
  }

  .aboutus-title-underline {
    width: 60px;
  }

  .aboutus-lab-section {
    margin-top: 60px;
  }

  .title {
    margin-bottom: 60px;
  }

  .odm-partner-dots {
    margin-top: 30px;
    margin-bottom: 30px;
  }

  .aboutus-lab-item {
    width: calc((100% - 3 * 20px - 8 * 10px) / 4);
    padding: 10px;
  }

  .arrow-image {
    width: 70%;
  }


}

/* 手机设备 */
@media screen and (max-width: 767px) {
  .aboutus-title {
    font-size: 1.8rem;
  }

  .aboutus-title-underline {
    width: 70px;
  }

  .aboutus-lab-label {
    font-size: 0.8rem;
  }

  .dot {
    width: 10px;
    height: 10px;
  }

  .aboutus-title-underline {
    width: 50px;
  }

  .aboutus-lab-section {
    margin-top: 50px;
  }

  .title {
    margin-bottom: 50px;
  }

  .odm-partner-dots {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .aboutus-lab-item {
    width: calc((100% - 3 * 10px - 8 * 5px) / 4);
    padding: 5px;
  }

  .aboutus-lab-grid {
    gap: 10px;
  }

  .arrow-image {
    width: 90%;
  }

}
</style>
